/**
 * Configuration for a route that should have state preserved
 */
export interface RouteStateConfig {
  /** The route path (can include wildcards) */
  path: string;

  /** Unique name for this route */
  name: string;

  /** Whether state preservation is enabled for this route */
  preserveState: boolean;

  /** Specific state keys to preserve (if not specified, all keys are preserved) */
  stateKeys?: string[];

  /** How long to cache state in milliseconds */
  cacheDuration: number;

  /** Storage strategy to use */
  storageStrategy?: StorageStrategy;

  /** Whether to preserve state across page refreshes */
  persistAcrossRefresh?: boolean;

  /** Whether to preserve scroll position */
  preserveScrollPosition?: boolean;

  /** Custom matcher for dynamic routes */
  pathMatcher?: (currentPath: string) => boolean;

  /** Conditions that must be met for state to be preserved */
  preserveCondition?: () => boolean;

  /** Priority for state restoration (higher = restored first) */
  priority?: number;

  /** Automatic property mapping for state capture/restore (avoids manual getState/setState) */
  propertyMap?: Record<string, string>;

  /** Post-restoration actions (e.g., row highlighting, DOM manipulation) */
  postRestoration?: PostRestorationConfig;
}

/**
 * Configuration for post-restoration actions
 */
export interface PostRestorationConfig {
  /** Enable row highlighting based on a state property */
  highlightRow?: {
    /** State property containing the row index */
    indexProperty: string;
    /** CSS selector for table rows (default: '.table-row') */
    rowSelector?: string;
    /** CSS class to apply (default: 'highlighted-row') */
    highlightClass?: string;
    /** Scroll highlighted row into view */
    scrollIntoView?: boolean;
    /** Automatically restore pagination to show the highlighted row */
    restorePagination?: boolean;
    /** Page size for pagination calculation (default: 10) */
    pageSize?: number;
  };

  /** Custom post-restoration callback */
  customAction?: (state: Record<string, any>, component: any) => void;
}

/**
 * Storage strategy options
 */
export enum StorageStrategy {
  /** Memory only - fastest, lost on refresh */
  MEMORY_ONLY = 'memory',

  /** Memory + SessionStorage - survives refresh, lost on tab close */
  SESSION = 'session',

  /** Memory + SessionStorage + LocalStorage - persists across sessions */
  LOCAL = 'local',

  /** Automatic selection based on cache duration */
  AUTO = 'auto'
}

/**
 * Helper to create route config with defaults
 */
export function createRouteConfig(
  config: Partial<RouteStateConfig> & Pick<RouteStateConfig, 'path' | 'name'>
): RouteStateConfig {
  return {
    preserveState: true,
    cacheDuration: 300000, // 5 minutes default
    storageStrategy: StorageStrategy.AUTO,
    persistAcrossRefresh: false,
    preserveScrollPosition: true,
    priority: 0,
    ...config
  };
}
